import { inject, Injectable } from '@angular/core';
import { flatten, groupBy } from 'lodash-es';
import { EMPTY, firstValueFrom, of } from 'rxjs';
import { expand, reduce, switchMap, tap } from 'rxjs/operators';

import {
  GqlService,
  PageInput,
  PatientProtocolType,
  UpdateSitePaymentScheduleInput,
  updateSitePaymentScheduleMutation,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { FormValuesQuery } from '@shared/store/form-values/form-values.query';
import { MainQuery } from '@shared/store/main/main.query';
import { batchPromises } from '@shared/utils';
import { Utils } from '@shared/utils/utils';

import {
  PaymentSchedulesModel,
  PaymentSchedulesState,
  PaymentSchedulesStore,
} from './payment-schedules.store';

type PaymentScheduleParams = {
  patient_group_id: string;
  patient_protocol_version_id: string;
  site_ids: string[];
};

@Injectable({ providedIn: 'root' })
export class PaymentSchedulesService {
  private formValuesQuery = inject(FormValuesQuery);
  private gqlService = inject(GqlService);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);
  private paymentSchedulesStore = inject(PaymentSchedulesStore);

  cache = new Map<string, PaymentSchedulesState[]>();

  add(paymentSchedule: PaymentSchedulesModel) {
    this.paymentSchedulesStore.add(paymentSchedule);
  }

  private cacheHash({
    patient_group_id,
    patient_protocol_version_id,
    site_id,
  }: Omit<PaymentScheduleParams, 'site_ids'> & {
    site_id: string;
  }) {
    return `${patient_group_id}-${site_id}-${patient_protocol_version_id}`;
  }

  filterPatientGroupId(patientGroupId: string): null | string {
    if (patientGroupId === 'visits_costs' || patientGroupId === 'invoiceables') {
      return null;
    }

    return patientGroupId;
  }

  // eslint-disable-next-line max-lines-per-function
  get(
    patient_protocol_types: PatientProtocolType[],
    { patient_group_id, patient_protocol_version_id, site_ids }: PaymentScheduleParams,
    isRefresh: string | undefined
  ) {
    return this.mainQuery.select('trialKey').pipe(
      // eslint-disable-next-line max-lines-per-function
      switchMap(() => {
        this.paymentSchedulesStore.setLoading(true);
        this.paymentSchedulesStore.remove(() => true);

        if (isRefresh) {
          this.cache.clear();
        }

        const values = this.formValuesQuery.getValuesByFormName('patientBudget');
        const new_site_ids = values?.site_ids?.length ? values.site_ids : site_ids;

        const remaining_site_ids = new_site_ids.filter((site_id) => {
          return !this.cache.has(
            this.cacheHash({ patient_group_id, patient_protocol_version_id, site_id })
          );
        });

        if (remaining_site_ids.length === 0 && !isRefresh) {
          this.setPaymentSchedules({
            patient_group_id,
            patient_protocol_version_id,
            site_ids: new_site_ids,
          });
          this.paymentSchedulesStore.setLoading(false);

          return of([]);
        }

        const page_input: PageInput = {
          limit: 3000,
          offset: 0,
        };

        return this.gqlService
          .fetchTrialSitePaymentSchedules$({
            page: page_input,
            patient_group_id: this.filterPatientGroupId(patient_group_id),
            patient_protocol_types,
            patient_protocol_version_id,
            site_ids: remaining_site_ids,
          })
          .pipe(
            expand(({ data, success }) => {
              if (success && data) {
                if (Number.isInteger(data.next_offset) && data.next_offset >= 0) {
                  page_input.offset = data.next_offset;

                  return this.gqlService.fetchTrialSitePaymentSchedules$({
                    page: page_input,
                    patient_group_id: this.filterPatientGroupId(patient_group_id),
                    patient_protocol_types,
                    patient_protocol_version_id,
                    site_ids: remaining_site_ids,
                  });
                }
              }

              return EMPTY;
            }),
            reduce(
              (acc, curr) => {
                if (acc.data && curr.success && curr.data) {
                  acc.data = acc.data.concat(curr.data.items);

                  return acc;
                }

                return {
                  data: null,
                  errors: curr.errors,
                  success: false,
                };
              },
              {
                data: [] as Array<PaymentSchedulesState> | null,
                errors: [] as string[],
                success: true,
              }
            ),
            tap(({ data, success }) => {
              if (success && data) {
                const groupedData = groupBy(data, 'site_id');

                for (const site_id of remaining_site_ids) {
                  const site_data = groupedData[site_id] || [];

                  this.cache.set(
                    this.cacheHash({ patient_group_id, patient_protocol_version_id, site_id }),
                    site_data
                  );
                }

                this.setPaymentSchedules({
                  patient_group_id,
                  patient_protocol_version_id,
                  site_ids: new_site_ids,
                });
              }

              this.paymentSchedulesStore.setLoading(false);
            })
          );
      })
    );
  }

  async getSiteSpecificPaymentSchedules(
    site_id: string,
    patient_protocol_types: PatientProtocolType[],
    patient_protocol_version_id?: string
  ) {
    const { data, errors, success } = await firstValueFrom(
      this.gqlService.listSitePaymentSchedules$(
        patient_protocol_types,
        site_id,
        patient_protocol_version_id
      )
    );

    this.paymentSchedulesStore.remove(() => true);

    if (success && data) {
      this.paymentSchedulesStore.add(
        data.map(({ expense_amount, id, note, patient_protocol }) => ({
          amount: expense_amount.amount,
          id,
          note,
          patient_protocol_id: patient_protocol.id,
          patient_protocol_type: patient_protocol.patient_protocol_type,
          site_id,
        }))
      );
    }

    return {
      data,
      errors,
      success,
    };
  }

  remove(id: string) {
    this.paymentSchedulesStore.remove(id);
  }

  private setPaymentSchedules({
    patient_group_id,
    patient_protocol_version_id,
    site_ids,
  }: PaymentScheduleParams) {
    const data = flatten(
      site_ids.map((site_id) => {
        return this.cache.get(
          this.cacheHash({ patient_group_id, patient_protocol_version_id, site_id })
        );
      })
    ) as PaymentSchedulesState[];

    this.paymentSchedulesStore.set(
      data.map(
        ({ expense_amount, id, note, patient_protocol, site_budget_version_name, site_id }) => ({
          amount: expense_amount.amount,
          amount_contract: expense_amount.contract_amount,
          id,
          note,
          patient_protocol_id: patient_protocol.id,
          patient_protocol_type: patient_protocol.patient_protocol_type,
          site_budget_version_name,
          site_id,
          sps_contract_expense_currency: expense_amount.contract_curr,
          sps_expense_currency: expense_amount.amount_curr,
        })
      )
    );
  }

  async update(upsertData: UpdateSitePaymentScheduleInput[], site_id: string) {
    const allErrors: string[][] = [];
    const proms: Promise<GraphqlResponse<updateSitePaymentScheduleMutation>>[] = [];

    for (const mil of upsertData) {
      const { amount, id, note, patient_protocol_id } = mil;

      if (id) {
        proms.push(
          firstValueFrom(
            this.gqlService
              .updateSitePaymentSchedule$({
                amount,
                id,
                note,
                patient_protocol_id,
              })
              .pipe(
                tap(({ data, errors, success }) => {
                  if (success && data) {
                    this.paymentSchedulesStore.update(data.id, {
                      ...data,
                      amount: data.expense_amount.amount,
                    });
                  } else {
                    allErrors.push(errors);
                  }
                })
              )
          )
        );
      } else {
        if (amount && Utils.isNumber(amount)) {
          proms.push(
            firstValueFrom(
              this.gqlService
                .createSitePaymentSchedule$({
                  amount: amount || 0,
                  note,
                  patient_protocol_id: patient_protocol_id || '',
                  site_budget_version_id: '', // empty string since it's calling only in  site modal that deprecated
                  site_id,
                })
                .pipe(
                  tap(({ data, errors, success }) => {
                    if (success && data) {
                      const { expense_amount, id: payment_schedule_id, patient_protocol } = data;

                      this.paymentSchedulesStore.add({
                        amount: expense_amount.amount,
                        id: payment_schedule_id,
                        note,
                        patient_protocol_id: patient_protocol.id,
                        patient_protocol_type: patient_protocol.patient_protocol_type,
                        site_id,
                      });
                    } else {
                      allErrors.push(errors);
                    }
                  })
                )
            )
          );
        }
      }
    }

    await batchPromises(proms, (p) => p);

    if (allErrors.length) {
      this.overlayService.error(...allErrors);
    }

    return !!allErrors.length;
  }
}
